import { z } from 'zod';
import { PersonRef } from './people.js';
import { Role } from './roles.js';
import { addDaysIso, minutesOfDay } from './time.js';
import { Priority, StatusCategory, WorkItemType } from './work-items.js';
/**
 * Project rules (docs/adr/0109-project-rules.md): When → Only if → Then on one project. A rule acts as the person who
 * last saved it, never sets off another rule, and runs once per event per task. Project roles are labels for rules.
 */
/** Limits, enforced on the server. */
export const RULES_PER_WORKSPACE = 50;
export const STEPS_PER_RULE = 10;
export const CONDITIONS_PER_RULE = 5;
/** History shown and kept, in days. */
export const RULE_HISTORY_DAYS = 30;
// --- Project roles --------------------------------------------------------------------------------------------------
export const ProjectRole = z.enum(['lead', 'developer', 'designer', 'tester', 'client_contact']);
export const PROJECT_ROLE_LABELS = {
    lead: 'Lead',
    developer: 'Developer',
    designer: 'Designer',
    tester: 'Tester',
    client_contact: 'Client contact',
};
export const ProjectRoleEntry = z.object({ user: PersonRef, access: Role, role: ProjectRole.nullable() });
export const ProjectRoles = z.object({
    items: z.array(ProjectRoleEntry),
    /** Owners, admins and the project's current Lead. */
    canSetRoles: z.boolean(),
});
export const SetProjectRoleInput = z.object({ role: ProjectRole.nullable() });
// --- A rule ---------------------------------------------------------------------------------------------------------
export const RuleTrigger = z.discriminatedUnion('kind', [
    /** Moved to this stage; `fromCategory` narrows it to moves out of a stage of that category ("Review back to In progress"). */
    z.object({ kind: z.literal('moved_to_stage'), statusId: z.uuid(), fromCategory: StatusCategory.nullable().optional() }),
    z.object({ kind: z.literal('moved_to_category'), category: StatusCategory, fromCategory: StatusCategory.nullable().optional() }),
    z.object({ kind: z.literal('created') }),
    z.object({ kind: z.literal('assigned') }),
    z.object({ kind: z.literal('overdue') }),
    z.object({ kind: z.literal('in_stage_for'), statusId: z.uuid(), days: z.number().int().min(1).max(60) }),
]);
const Label = z.string().trim().min(1).max(32);
export const RuleCondition = z.discriminatedUnion('field', [
    z.object({ field: z.literal('type'), values: z.array(WorkItemType).min(1).max(4) }),
    z.object({ field: z.literal('priority'), values: z.array(Priority).min(1).max(4) }),
    z.object({ field: z.literal('label'), values: z.array(Label).min(1).max(10) }),
    /** People by id, or `none` for "nobody". */
    z.object({ field: z.literal('assignee'), values: z.array(z.union([z.uuid(), z.literal('none')])).min(1).max(20) }),
    z.object({ field: z.literal('section'), values: z.array(z.uuid()).min(1).max(20) }),
]);
export const RuleTarget = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('person'), userId: z.uuid() }),
    z.object({ kind: z.literal('assignee') }),
    /** The person who moved, created or assigned the task. Nobody for the scheduled triggers. */
    z.object({ kind: z.literal('mover') }),
    z.object({ kind: z.literal('role'), role: ProjectRole }),
]);
export const RuleStep = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('notify'), to: RuleTarget }),
    /** `{task}` is the task's title and `{key}` its key. `for` null leaves it unassigned. */
    z.object({
        kind: z.literal('create_task'),
        title: z.string().trim().min(1).max(200),
        for: RuleTarget.nullable(),
        dueInDays: z.number().int().min(0).max(60).nullable(),
    }),
    z.object({ kind: z.literal('assign'), to: RuleTarget }),
    z.object({ kind: z.literal('move'), statusId: z.uuid() }),
    z.object({ kind: z.literal('add_label'), label: Label }),
    z.object({ kind: z.literal('set_due'), inDays: z.number().int().min(0).max(365) }),
    /** Posted as "Chapsi (rule)" in the project's channel. `{task}` and `{key}` as above. */
    z.object({ kind: z.literal('post'), text: z.string().trim().min(1).max(500) }),
]);
export const RuleTemplateKey = z.enum(['qa', 'deploy', 'client_live', 'handback', 'overdue_p1', 'triage']);
export const RuleInput = z
    .object({
    name: z.string().trim().min(1).max(120),
    enabled: z.boolean().default(true),
    trigger: RuleTrigger,
    conditions: z.array(RuleCondition).max(CONDITIONS_PER_RULE).default([]),
    steps: z.array(RuleStep).min(1).max(STEPS_PER_RULE),
    template: RuleTemplateKey.nullable().optional(),
})
    .refine((r) => new Set(r.conditions.map((c) => c.field)).size === r.conditions.length, { message: 'Use each condition once', path: ['conditions'] });
export const SwitchRuleInput = z.object({ enabled: z.boolean() });
export const RuleProblem = z.object({
    code: z.enum(['paused', 'step_failed', 'role_fallback', 'needs_person']),
    /** The step it is about (0-based), or null for the whole rule. */
    step: z.number().int().nullable(),
    text: z.string(),
    fix: z.string(),
});
export const Rule = z.object({
    id: z.uuid(),
    projectId: z.uuid(),
    name: z.string(),
    enabled: z.boolean(),
    trigger: RuleTrigger,
    conditions: z.array(RuleCondition),
    steps: z.array(RuleStep),
    template: RuleTemplateKey.nullable(),
    /** Who the rule acts as. Null once that person's account is gone. */
    savedBy: PersonRef.nullable(),
    updatedAt: z.string(),
    runs30d: z.number().int(),
    lastRunAt: z.string().nullable(),
    paused: z.boolean(),
    problems: z.array(RuleProblem),
});
export const RuleList = z.object({
    items: z.array(Rule),
    /** Owners, admins and the project's Lead create and change rules; everyone else who sees them reads. */
    canManage: z.boolean(),
    limits: z.object({ used: z.number().int(), max: z.number().int(), maxSteps: z.number().int() }),
});
export const StepOutcome = z.enum(['done', 'would_do', 'skipped', 'failed', 'needs_person', 'fallback']);
export const TryRuleInput = z.object({ rule: RuleInput, workItemId: z.uuid() });
export const TryRuleResult = z.object({
    /** Whether the task passes the conditions (the trigger is assumed to have happened). */
    matches: z.boolean(),
    reason: z.string().nullable(),
    steps: z.array(z.object({ position: z.number().int(), kind: z.string(), outcome: StepOutcome, text: z.string() })),
    /** Always true: a dry run sends nothing and changes nothing. */
    sentNothing: z.literal(true),
});
export const RuleRun = z.object({
    id: z.uuid(),
    at: z.string(),
    workItem: z.object({ id: z.uuid(), key: z.string(), title: z.string() }).nullable(),
    status: z.enum(['ran', 'skipped', 'failed']),
    text: z.string(),
});
export const RuleHistory = z.object({ rule: Rule, items: z.array(RuleRun), canManage: z.boolean() });
/** On a task's activity entry of kind `rule`: the rule, and whether the viewer may open it. */
export const ActivityRuleRef = z.object({ id: z.uuid(), name: z.string(), projectId: z.uuid(), canOpen: z.boolean() });
// --- Shared logic: matching, text, working days ---------------------------------------------------------------------
export const CATEGORY_LABELS = {
    backlog: 'Backlog', todo: 'To do', active: 'In progress', review: 'Review', blocked: 'Blocked', waiting: 'Waiting', done: 'Done',
};
const TYPE_LABELS = { task: 'Task', job: 'Job', support: 'Ticket', bug: 'Bug' };
/** Whether an event-driven trigger matches what happened. The scheduled triggers never match an event. */
export function triggerMatchesEvent(t, e) {
    switch (t.kind) {
        case 'moved_to_stage':
            return e.kind === 'moved' && e.toStatusId === t.statusId && (!t.fromCategory || e.fromCategory === t.fromCategory);
        case 'moved_to_category':
            return e.kind === 'moved' && e.toCategory === t.category && (!t.fromCategory || e.fromCategory === t.fromCategory);
        case 'created':
            return e.kind === 'created';
        case 'assigned':
            return e.kind === 'assigned';
        default:
            return false;
    }
}
/** The first condition the task fails, or null when it passes them all. */
export function failedCondition(conditions, task) {
    for (const c of conditions) {
        switch (c.field) {
            case 'type':
                if (!c.values.includes(task.type))
                    return c.field;
                break;
            case 'priority':
                if (!c.values.includes(task.priority))
                    return c.field;
                break;
            case 'label': {
                const has = new Set(task.labels.map((l) => l.toLowerCase()));
                if (!c.values.some((v) => has.has(v.toLowerCase())))
                    return c.field;
                break;
            }
            case 'assignee':
                if (!c.values.includes(task.assigneeUserId ?? 'none'))
                    return c.field;
                break;
            case 'section':
                if (!task.sectionId || !c.values.includes(task.sectionId))
                    return c.field;
                break;
        }
    }
    return null;
}
/** `{task}` → the task's title, `{key}` → its key; then trimmed to `max`. */
export function renderRuleText(template, task, max = 500) {
    const out = template.replace(/\{task\}/g, task.title).replace(/\{key\}/g, task.key).trim();
    return out.length > max ? `${out.slice(0, max - 1)}…` : out;
}
/** The date `n` working days after `isoDate` (a local date), skipping days that are not working days. 0 is the day itself. */
export function addWorkingDays(isoDate, n, workingDays) {
    const days = workingDays.length ? workingDays : [1, 2, 3, 4, 5];
    let d = isoDate;
    let left = Math.max(0, Math.floor(n));
    for (let guard = 0; left > 0 && guard < 3660; guard++) {
        d = addDaysIso(d, 1);
        if (days.includes(new Date(`${d}T00:00:00Z`).getUTCDay()))
            left--;
    }
    return d;
}
/** Minutes in one working day of these hours (a shift past midnight included). */
export function workingDayMinutes(hours) {
    const start = minutesOfDay(hours.start);
    const end = minutesOfDay(hours.end);
    const m = end > start ? end - start : 1440 - start + end;
    return m > 0 ? m : 1440;
}
const or = (xs) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} or ${xs[xs.length - 1]}`);
const and = (xs) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);
const days = (n) => `${n} working day${n === 1 ? '' : 's'}`;
export function targetText(t, l) {
    if (!t)
        return 'nobody';
    switch (t.kind) {
        case 'person':
            return l.person(t.userId) ?? 'someone';
        case 'assignee':
            return 'the assignee';
        case 'mover':
            return 'the person who moved it';
        case 'role': {
            const who = l.roleHolders?.(t.role) ?? [];
            return who.length ? `${and(who)} (the ${PROJECT_ROLE_LABELS[t.role]})` : `the project’s ${PROJECT_ROLE_LABELS[t.role]}`;
        }
    }
}
export function triggerText(t, l) {
    const from = 'fromCategory' in t && t.fromCategory ? ` from a ${CATEGORY_LABELS[t.fromCategory]} stage` : '';
    switch (t.kind) {
        case 'moved_to_stage':
            return `moves${from} to ${l.stage(t.statusId) ?? 'a stage'}`;
        case 'moved_to_category':
            return `moves${from} to any ${CATEGORY_LABELS[t.category]} stage`;
        case 'created':
            return 'is created';
        case 'assigned':
            return 'is assigned';
        case 'overdue':
            return 'becomes overdue';
        case 'in_stage_for':
            return `has been in ${l.stage(t.statusId) ?? 'a stage'} for ${days(t.days)}`;
    }
}
export function stepText(s, l) {
    switch (s.kind) {
        case 'notify':
            return `notifies ${targetText(s.to, l)}`;
        case 'create_task':
            return `creates “${s.title}”${s.for ? ` for ${targetText(s.for, l)}` : ''}${s.dueInDays === null ? '' : s.dueInDays === 0 ? ', due today' : `, due in ${days(s.dueInDays)}`}`;
        case 'assign':
            return `assigns it to ${targetText(s.to, l)}`;
        case 'move':
            return `moves it to ${l.stage(s.statusId) ?? 'a stage'}`;
        case 'add_label':
            return `adds the label “${s.label}”`;
        case 'set_due':
            return s.inDays === 0 ? 'sets it due today' : `sets it due in ${days(s.inDays)}`;
        case 'post':
            return `posts in ${l.channel ? `#${l.channel}` : 'the project’s chat'}`;
    }
}
function conditionText(c, l) {
    switch (c.field) {
        case 'type':
            return `it’s a ${or(c.values.map((v) => TYPE_LABELS[v]))}`;
        case 'priority':
            return `its priority is ${or(c.values.map((v) => v.toUpperCase()))}`;
        case 'label':
            return `it’s labelled ${or(c.values.map((v) => `“${v}”`))}`;
        case 'assignee':
            return `it’s assigned to ${or(c.values.map((v) => (v === 'none' ? 'nobody' : l.person(v) ?? 'someone')))}`;
        case 'section':
            return `it’s in ${or(c.values.map((v) => l.section?.(v) ?? 'a section'))}`;
    }
}
/** The plain-English summary: "When a task moves to Ready for QA and it’s a Task or Bug, Chapsi notifies Mia (the Tester)…". */
export function describeRule(rule, l) {
    const when = `When a task ${triggerText(rule.trigger, l)}`;
    const only = rule.conditions.length ? ` and ${and(rule.conditions.map((c) => conditionText(c, l)))}` : '';
    const then = rule.steps.length ? `, Chapsi ${and(rule.steps.map((s) => stepText(s, l)))}` : ', Chapsi does nothing yet';
    return `${when}${only}${then}.`;
}
export const RULE_TEMPLATES = [
    { key: 'qa', tag: 'QA', name: 'Notify the tester when work is ready for QA', sentence: 'When a task moves to Ready for QA → notify the project’s Tester.' },
    { key: 'deploy', tag: 'DEPLOY', name: 'Create a deploy task when work is approved', sentence: 'When a task moves to Done → create a deploy task for the Lead.' },
    { key: 'client_live', tag: 'CLIENT', name: 'Tell the client contact when it’s live', sentence: 'When a task moves to Production → notify the Client contact.' },
    { key: 'handback', tag: 'HANDOVER', name: 'Hand back to the developer on changes', sentence: 'When a task moves from Review back to In progress → assign the Developer.' },
    { key: 'overdue_p1', tag: 'OVERDUE', name: 'Escalate overdue P1s', sentence: 'When a P1 becomes overdue → notify the Lead and post in the project chat.' },
    { key: 'triage', tag: 'INTAKE', name: 'Triage new bugs', sentence: 'When a Bug is created → add the label Needs triage and assign the Lead.' },
];
/** A template filled in from the project's own stages. */
export function templateDraft(key, stages) {
    const def = RULE_TEMPLATES.find((t) => t.key === key);
    const find = (test) => stages.find(test);
    const base = { name: def.name, enabled: true, template: key, conditions: [] };
    switch (key) {
        case 'qa': {
            const stage = find((s) => /\bqa\b/i.test(s.label)) ?? find((s) => s.key === 'review') ?? find((s) => s.category === 'review') ?? stages[0];
            return {
                ...base,
                name: stage ? `${stage.label}: tell the tester` : def.name,
                trigger: stage ? { kind: 'moved_to_stage', statusId: stage.id } : { kind: 'moved_to_category', category: 'review' },
                steps: [{ kind: 'notify', to: { kind: 'role', role: 'tester' } }, { kind: 'post', text: `{key} is ready for ${stage && /\bqa\b/i.test(stage.label) ? 'QA' : 'review'}.` }],
            };
        }
        case 'deploy':
            return {
                ...base,
                name: 'Approved: create the deploy task',
                trigger: { kind: 'moved_to_category', category: 'done' },
                conditions: [{ field: 'type', values: ['task', 'bug'] }],
                steps: [{ kind: 'create_task', title: 'Deploy {task} to production', for: { kind: 'role', role: 'lead' }, dueInDays: 2 }],
            };
        case 'client_live': {
            const stage = find((s) => s.key === 'production') ?? find((s) => /production|live/i.test(s.label));
            return {
                ...base,
                name: 'Live: tell the client contact',
                trigger: stage ? { kind: 'moved_to_stage', statusId: stage.id } : { kind: 'moved_to_category', category: 'done' },
                steps: [{ kind: 'notify', to: { kind: 'role', role: 'client_contact' } }],
            };
        }
        case 'handback':
            return {
                ...base,
                name: 'Changes asked: back to the developer',
                trigger: { kind: 'moved_to_category', category: 'active', fromCategory: 'review' },
                steps: [{ kind: 'assign', to: { kind: 'role', role: 'developer' } }],
            };
        case 'overdue_p1':
            return {
                ...base,
                name: 'Overdue P1: tell the Lead',
                trigger: { kind: 'overdue' },
                conditions: [{ field: 'priority', values: ['p1'] }],
                steps: [{ kind: 'notify', to: { kind: 'role', role: 'lead' } }, { kind: 'post', text: '{key} is overdue.' }],
            };
        case 'triage':
            return {
                ...base,
                name: 'New bug: triage',
                trigger: { kind: 'created' },
                conditions: [{ field: 'type', values: ['bug'] }],
                steps: [{ kind: 'add_label', label: 'Needs triage' }, { kind: 'assign', to: { kind: 'role', role: 'lead' } }],
            };
    }
}
//# sourceMappingURL=rules.js.map